import axios from "axios";

import { API_ROUTES } from "../api/ApiRoutes";
import { getPrivateInstance } from "../queries/_privateInstance";

/**
 * S3 image upload helper for promotional banner creatives (BONENG-522), used
 * by the campaign banner editor's "Upload Image" button.
 *
 * Two-step flow (mirrors `gamePassImageUpload.presignAndUploadToS3`):
 *   1. `POST /admin-panel-v2/promotions/carousel-banners/presigned-url`
 *      → `{ upload_url, image_url, ... }` (the backend mints the S3 key).
 *   2. `PUT <upload_url>` with the raw bytes via a bare axios call — no auth
 *      interceptor, because the presigned URL's signature is the only auth S3
 *      accepts — and the `Content-Type` header we signed with.
 *
 * Resolves with the CloudFront `image_url` the banner design stores in
 * `image.url`. The backend only signs PNG uploads and re-validates size/type
 * server-side; the client checks here (type, size) exist for fast, friendly
 * feedback.
 *
 * Uploading only presigns + PUTs to S3 — it never touches any campaign API,
 * so it cannot create, activate, or send a campaign.
 */

/** Backend signs PNG only. */
export const BANNER_IMAGE_CONTENT_TYPE = "image/png";

/** 250 KB creative cap, mirrored from the backend (BONENG-522). */
export const MAX_BANNER_IMAGE_BYTES = 250 * 1024;

/** Subset of the presign response the upload flow depends on. */
interface BannerPresignResponse {
  upload_url?: string;
  /** CloudFront URL the creative will be served from after the PUT. */
  image_url?: string;
}

type PresignEnvelope = {
  data?: BannerPresignResponse;
};

/**
 * Map transport failures to operator-friendly messages instead of surfacing
 * axios's "Request failed with status code NNN". A 401 means the staff session
 * expired; a 403 means the account lacks the `promotions.carousel_banners`
 * permission the presign route is gated on.
 */
const toFriendlyUploadError = (error: unknown): Error => {
  // The mapped message hides the status/body — keep the raw failure
  // diagnosable from the console.
  console.error("Banner image upload failed:", error);
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401) {
    return new Error("Your session has expired — please log in again");
  }
  if (status === 403) {
    return new Error("You don't have permission to upload banner images");
  }
  return new Error("Failed to upload image — please try again");
};

export const uploadBannerImage = async (file: File): Promise<string> => {
  if (file.type !== BANNER_IMAGE_CONTENT_TYPE) {
    throw new Error("Banner image must be a PNG");
  }
  if (file.size > MAX_BANNER_IMAGE_BYTES) {
    throw new Error(
      `Banner image must be ${Math.floor(MAX_BANNER_IMAGE_BYTES / 1024)} KB or smaller`,
    );
  }

  let presigned: BannerPresignResponse;
  try {
    const presignResponse = await getPrivateInstance().post<PresignEnvelope>(
      API_ROUTES.CAROUSEL_BANNER_PRESIGN,
      { content_type: BANNER_IMAGE_CONTENT_TYPE },
    );
    presigned = presignResponse?.data?.data ?? {};
  } catch (error) {
    throw toFriendlyUploadError(error);
  }

  if (!presigned.upload_url || !presigned.image_url) {
    throw new Error("Failed to generate a pre-signed upload URL");
  }

  try {
    await axios.put(presigned.upload_url, file, {
      headers: { "Content-Type": BANNER_IMAGE_CONTENT_TYPE },
    });
  } catch (error) {
    throw toFriendlyUploadError(error);
  }

  return presigned.image_url;
};
